import { cmToMm } from '../../utils/number-converter';
import NumberFormatter from '../../utils/number-formatter';

export class Measurement {
  geometry = com.parkside.measure_geometry;
  textColor = '#000';
  textFontFamily = 'sans-serif';
  textFontSize = '12px';

  constructor(options) {
    ({
      canvas: this.canvas,
      view: this.view,
      points: this.points,
      additionalPoints: this.additionalPoints,
      planarea: this.planarea,
      offset: this.offset = 0,
      style: this.style = MeasurementVisualStyle.normal,
      formatPatternScale: this.formatPatternScale,
    } = options);
    this.group = options.parentGroup.createGroup();
    this.points = this.planarea ? this.planarea.poly.points : this.points;
    this.numberFormatter = new NumberFormatter({});
  }

  rotateText(textShape, degree) {
    let bbox = textShape.getBoundingBox(),
      rotatex = bbox.x + bbox.width * 0.5,
      rotatey = bbox.y + bbox.height * 0.5;
    textShape.setTransform([dojox.gfx.matrix.rotategAt(degree, rotatex, rotatey)]);
  }

  setNewPoints(points, additionalPoints) {
    this.points = points;
    this.additionalPoints = additionalPoints;
  }

  render() {
    this.group.clear();

    if (this.planarea && !this.planarea.activated) {
      return;
    }

    const polypoints = this.points,
      polytop = this.geometry.find_poly_top(polypoints),
      polybottom = this.geometry.find_poly_bottom(polypoints),
      polyleft = this.geometry.find_poly_left(polypoints),
      polyright = this.geometry.find_poly_right(polypoints);

    const containerPosition = dojo.position(this.canvas, true);

    const verticalPoints = this._findScalePoints(polytop, polybottom);
    const horizontalPoints = this._findScalePoints(polyleft, polyright);
    if (this.additionalPoints) {
      verticalPoints.firstGroup.push(...this.additionalPoints);
      verticalPoints.secondGroup.push(...this.additionalPoints);
      horizontalPoints.firstGroup.push(...this.additionalPoints);
      horizontalPoints.secondGroup.push(...this.additionalPoints);
    }

    this._renderLeft(containerPosition, this.geometry.arrayOfPointsSort(verticalPoints.firstGroup, 'y'));
    this._renderRight(containerPosition, this.geometry.arrayOfPointsSort(verticalPoints.secondGroup, 'y'));
    this._renderTop(containerPosition, this.geometry.arrayOfPointsSort(horizontalPoints.firstGroup, 'x'));
    this._renderBottom(containerPosition, this.geometry.arrayOfPointsSort(horizontalPoints.secondGroup, 'x'));
  }

  _findScalePoints(startPoly, endPoly) {
    const polypoints = this.points;
    const direction = this.geometry.find_poly_direction(polypoints);
    let leftpoints = [],
      rightpoints = [],
      current = 'left';

    for (let i = 0; i <= polypoints.length; i++) {
      let cidx = this.geometry.wrapidx(startPoly + direction * i, polypoints.length);
      let point = polypoints[cidx];
      if (current === 'left') {
        leftpoints.push(point);
      } else {
        rightpoints.push(point);
      }

      if (cidx === endPoly) {
        current = 'right';
        rightpoints.push(point);
      }
    }
    return {
      firstGroup: leftpoints,
      secondGroup: rightpoints,
    };
  }

  _renderLeft(containerPosition, leftPoints) {
    const group = this.group.createGroup();
    group.createRect({ x: 0, y: 0, width: 30, height: containerPosition.h }).setFill([255, 255, 255, 0.8]);
    group
      .createLine({
        x1: 20,
        y1: this.view.convertGlobalToLocal(leftPoints[0]).y,
        x2: 20,
        y2: this.view.convertGlobalToLocal(leftPoints[leftPoints.length - 1]).y,
      })
      .setStroke(this._getScaleLineOptions());

    let smallMeasuresSum = 0;
    let smallMeasuresCount = 1;
    for (let i = 1; i < leftPoints.length; i++) {
      let oppoint = leftPoints[i - 1],
        opoint = leftPoints[i],
        ppoint = this.view.convertGlobalToLocal(oppoint),
        point = this.view.convertGlobalToLocal(opoint);

      if (i === 1) {
        group
          .createLine({
            x1: 12,
            y1: ppoint.y,
            x2: 28,
            y2: ppoint.y,
          })
          .setStroke('black');
      }
      if (Math.abs(opoint.y - oppoint.y) > 5) {
        group
          .createLine({
            x1: 12,
            y1: point.y,
            x2: 28,
            y2: point.y,
          })
          .setStroke('black');
        let text = group
          .createText({
            x: 6,
            y: ppoint.y + (point.y - ppoint.y) * 0.5 + 5.0,
            text: this.numberFormatter.formatOutput(
              cmToMm(Math.round(Math.abs(opoint.y - oppoint.y))),
              this.formatPatternScale,
              false
            ),
            align: 'middle',
          })
          .setFill(this.textColor)
          .setFont({ family: this.textFontFamily, size: this.textFontSize });
        this.rotateText(text, -90);
      } else {
        if (
          (leftPoints.length > i + 1 && Math.abs(leftPoints[i + 1].y - leftPoints[i].y) > 5) ||
          i === leftPoints.length - 1
        ) {
          if (Math.round(Math.abs(opoint.y - oppoint.y) + smallMeasuresSum) > 0) {
            group
              .createLine({
                x1: 12,
                y1: point.y,
                x2: 28,
                y2: point.y,
              })
              .setStroke('black');
            let texty =
              this.view.convertGlobalToLocal(leftPoints[i - smallMeasuresCount]).y +
              (point.y - this.view.convertGlobalToLocal(leftPoints[i - smallMeasuresCount]).y) * 0.5;
            let textx = 5;
            if (Math.round(Math.abs(opoint.y - oppoint.y) + smallMeasuresSum) > 5) {
              textx = 10;
            }
            let text = group
              .createText({
                x: textx,
                y: texty + 5.0,
                text: this.numberFormatter.formatOutput(
                  cmToMm(Math.round(Math.abs(opoint.y - oppoint.y) + smallMeasuresSum)),
                  this.formatPatternScale,
                  false
                ),
                align: 'middle',
              })
              .setFill(this.textColor)
              .setFont({ family: this.textFontFamily, size: this.textFontSize });
            this.rotateText(text, -90);
          }
          smallMeasuresSum = 0;
          smallMeasuresCount = 1;
        } else {
          smallMeasuresSum += Math.abs(opoint.y - oppoint.y);
          smallMeasuresCount++;
        }
      }
    }
    if (this.offset) {
      group.applyTransform([dojox.gfx.matrix.translate(this.offset, 0)]);
    }
  }

  _renderRight(containerPosition, rightPoints) {
    const group = this.group.createGroup();
    const width = 30;
    group
      .createRect({
        x: containerPosition.w - width,
        y: 0,
        width: width,
        height: containerPosition.h,
      })
      .setFill([255, 255, 255, 0.8]);
    group
      .createLine({
        x1: containerPosition.w - (width - 20),
        y1: this.view.convertGlobalToLocal(rightPoints[0]).y,
        x2: containerPosition.w - (width - 20),
        y2: this.view.convertGlobalToLocal(rightPoints[rightPoints.length - 1]).y,
      })
      .setStroke(this._getScaleLineOptions());

    let smallMeasuresSum = 0;
    let smallMeasuresCount = 1;
    for (let i = 1; i < rightPoints.length; i++) {
      let oppoint = rightPoints[i - 1],
        opoint = rightPoints[i],
        ppoint = this.view.convertGlobalToLocal(oppoint),
        point = this.view.convertGlobalToLocal(opoint);

      if (i === 1) {
        group
          .createLine({
            x1: containerPosition.w - (width - 28),
            y1: ppoint.y,
            x2: containerPosition.w - (width - 12),
            y2: ppoint.y,
          })
          .setStroke('black');
      }
      if (Math.abs(opoint.y - oppoint.y) > 5) {
        group
          .createLine({
            x1: containerPosition.w - (width - 28),
            y1: point.y,
            x2: containerPosition.w - (width - 12),
            y2: point.y,
          })
          .setStroke('black');
        let text = group
          .createText({
            x: containerPosition.w - (width - 6),
            y: ppoint.y + (point.y - ppoint.y) * 0.5 + 5.0,
            text: this.numberFormatter.formatOutput(
              cmToMm(Math.round(Math.abs(opoint.y - oppoint.y))),
              this.formatPatternScale,
              false
            ),
            align: 'middle',
          })
          .setFill(this.textColor)
          .setFont({ family: this.textFontFamily, size: this.textFontSize });
        this.rotateText(text, -90);
      } else {
        if (
          (rightPoints.length > i + 1 && Math.abs(rightPoints[i + 1].y - rightPoints[i].y) > 5) ||
          i === rightPoints.length - 1
        ) {
          if (Math.round(Math.abs(opoint.y - oppoint.y) + smallMeasuresSum) > 0) {
            group
              .createLine({
                x1: containerPosition.w - (width - 28),
                y1: point.y,
                x2: containerPosition.w - (width - 12),
                y2: point.y,
              })
              .setStroke('black');
            let texty =
              this.view.convertGlobalToLocal(rightPoints[i - smallMeasuresCount]).y +
              (point.y - this.view.convertGlobalToLocal(rightPoints[i - smallMeasuresCount]).y) * 0.5;
            let textx = containerPosition.w - width;
            if (Math.round(Math.abs(opoint.y - oppoint.y) + smallMeasuresSum) > 5) {
              textx = containerPosition.w - 72;
            }

            let text = group
              .createText({
                x: textx,
                y: texty + 5.0,
                text: this.numberFormatter.formatOutput(
                  cmToMm(Math.round(Math.abs(opoint.y - oppoint.y) + smallMeasuresSum)),
                  this.formatPatternScale,
                  false
                ),
                align: 'middle',
              })
              .setFill(this.textColor)
              .setFont({ family: this.textFontFamily, size: this.textFontSize });
            this.rotateText(text, -90);
          }
          smallMeasuresSum = 0;
          smallMeasuresCount = 1;
        } else {
          smallMeasuresSum += Math.abs(opoint.y - oppoint.y);
          smallMeasuresCount++;
        }
      }
    }
    if (this.offset) {
      group.applyTransform([dojox.gfx.matrix.translate(-this.offset, 0)]);
    }
  }

  _renderTop(containerPosition, leftPoints) {
    const group = this.group.createGroup();
    group
      .createRect({
        x: 30,
        y: 0,
        width: containerPosition.w - 110,
        height: 30,
      })
      .setFill([255, 255, 255, 0.8]);
    group
      .createLine({
        x1: this.view.convertGlobalToLocal(leftPoints[0]).x,
        y1: 20,
        x2: this.view.convertGlobalToLocal(leftPoints[leftPoints.length - 1]).x,
        y2: 20,
      })
      .setStroke(this._getScaleLineOptions());

    let smallMeasuresSum = 0;
    let smallMeasuresCount = 1;
    for (let i = 1; i < leftPoints.length; i++) {
      let oppoint = leftPoints[i - 1],
        opoint = leftPoints[i],
        ppoint = this.view.convertGlobalToLocal(oppoint),
        point = this.view.convertGlobalToLocal(opoint);

      if (i === 1) {
        group.createLine({ x1: ppoint.x, y1: 12, x2: ppoint.x, y2: 28 }).setStroke('black');
      }

      if (Math.abs(opoint.x - oppoint.x) > 5) {
        group.createLine({ x1: point.x, y1: 12, x2: point.x, y2: 28 }).setStroke('black');
        group
          .createText({
            x: ppoint.x + (point.x - ppoint.x) * 0.5,
            y: 10,
            text: this.numberFormatter.formatOutput(
              cmToMm(Math.round(Math.abs(opoint.x - oppoint.x))),
              this.formatPatternScale,
              false
            ),
            align: 'middle',
          })
          .setFill(this.textColor)
          .setFont({ family: this.textFontFamily, size: this.textFontSize });
      } else {
        if (
          (leftPoints.length > i + 1 && Math.abs(leftPoints[i + 1].x - leftPoints[i].x) > 5) ||
          i === leftPoints.length - 1
        ) {
          if (Math.round(Math.abs(opoint.x - oppoint.x) + smallMeasuresSum) > 0) {
            group
              .createLine({
                x1: point.x,
                y1: 12,
                x2: point.x,
                y2: 28,
              })
              .setStroke('black');

            let texty = 9;
            if (Math.round(Math.abs(opoint.x - oppoint.x) + smallMeasuresSum) > 5) {
              texty = 13;
            }
            let textx =
              this.view.convertGlobalToLocal(leftPoints[i - smallMeasuresCount]).x +
              (point.x - this.view.convertGlobalToLocal(leftPoints[i - smallMeasuresCount]).x) * 0.5;

            group
              .createText({
                x: textx,
                y: texty,
                text: this.numberFormatter.formatOutput(
                  cmToMm(Math.round(Math.abs(opoint.x - oppoint.x) + smallMeasuresSum)),
                  this.formatPatternScale,
                  false
                ),
                align: 'middle',
              })
              .setFill(this.textColor)
              .setFont({ family: this.textFontFamily, size: this.textFontSize });
          }
          smallMeasuresSum = 0;
          smallMeasuresCount = 1;
        } else {
          smallMeasuresSum += Math.abs(opoint.x - oppoint.x);
          smallMeasuresCount++;
        }
      }
    }
    if (this.offset) {
      group.applyTransform([dojox.gfx.matrix.translate(0, this.offset)]);
    }
  }

  _renderBottom(containerPosition, points) {
    const group = this.group.createGroup();
    group
      .createRect({
        x: 30,
        y: containerPosition.h - 30,
        width: containerPosition.w - 110,
        height: 30,
      })
      .setFill([255, 255, 255, 0.8]);
    group
      .createLine({
        x1: this.view.convertGlobalToLocal(points[0]).x,
        y1: containerPosition.h - 10,
        x2: this.view.convertGlobalToLocal(points[points.length - 1]).x,
        y2: containerPosition.h - 10,
      })
      .setStroke(this._getScaleLineOptions());

    let smallMeasuresSum = 0;
    let smallMeasuresCount = 1;
    for (let i = 1; i < points.length; i++) {
      let oppoint = points[i - 1],
        opoint = points[i],
        ppoint = this.view.convertGlobalToLocal(oppoint),
        point = this.view.convertGlobalToLocal(opoint);

      if (i === 1) {
        group
          .createLine({
            x1: ppoint.x,
            y1: containerPosition.h - 18,
            x2: ppoint.x,
            y2: containerPosition.h - 2,
          })
          .setStroke('black');
      }

      if (Math.abs(opoint.x - oppoint.x) > 5) {
        group
          .createLine({
            x1: point.x,
            y1: containerPosition.h - 18,
            x2: point.x,
            y2: containerPosition.h - 2,
          })
          .setStroke('black');
        group
          .createText({
            x: ppoint.x + (point.x - ppoint.x) * 0.5,
            y: containerPosition.h - 20,
            text: this.numberFormatter.formatOutput(
              cmToMm(Math.round(Math.abs(opoint.x - oppoint.x))),
              this.formatPatternScale,
              false
            ),
            align: 'middle',
          })
          .setFill(this.textColor)
          .setFont({ family: this.textFontFamily, size: this.textFontSize });
      } else {
        if ((points.length > i + 1 && Math.abs(points[i + 1].x - points[i].x) > 5) || i === points.length - 1) {
          if (Math.round(Math.abs(opoint.x - oppoint.x) + smallMeasuresSum) > 0) {
            group
              .createLine({
                x1: point.x,
                y1: containerPosition.h - 20,
                x2: point.x,
                y2: containerPosition.h,
              })
              .setStroke('black');

            let texty = containerPosition.h - 21;
            if (Math.round(Math.abs(opoint.x - oppoint.x) + smallMeasuresSum) > 5) {
              texty = containerPosition.h - 17;
            }
            let textx =
              this.view.convertGlobalToLocal(points[i - smallMeasuresCount]).x +
              (point.x - this.view.convertGlobalToLocal(points[i - smallMeasuresCount]).x) * 0.5;

            group
              .createText({
                x: textx,
                y: texty,
                text: this.numberFormatter.formatOutput(
                  cmToMm(Math.round(Math.abs(opoint.x - oppoint.x) + smallMeasuresSum)),
                  this.formatPatternScale,
                  false
                ),
                align: 'middle',
              })
              .setFill(this.textColor)
              .setFont({ family: this.textFontFamily, size: this.textFontSize });
          }
          smallMeasuresSum = 0;
          smallMeasuresCount = 1;
        } else {
          smallMeasuresSum += Math.abs(opoint.x - oppoint.x);
          smallMeasuresCount++;
        }
      }
    }
    if (this.offset) {
      group.applyTransform([dojox.gfx.matrix.translate(0, -this.offset)]);
    }
  }

  _getScaleLineOptions() {
    let style = 'solid';
    if (this.style === MeasurementVisualStyle.splitting) {
      style = 'longdash';
    }
    return { color: 'black', style: style };
  }
}

export const MeasurementVisualStyle = Object.freeze({
  normal: Symbol('normal'),
  splitting: Symbol('splitting'),
});
